import { ref, watch } from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { useRoute } from 'vue-router';

/**
 * NavBar 展开/收起状态管理
 * 统一管理侧边栏的自动收起逻辑
 */
export const useNavBar = createSharedComposable(() => {
    const route = useRoute();

    // 侧边栏是否收起
    const isCollapsed = ref(false);

    /**
     * 切换侧边栏展开/收起状态
     */
    const toggleSidebar = () => {
        isCollapsed.value = !isCollapsed.value;
    };

    /**
     * 设置侧边栏收起状态
     */
    const setCollapsed = (collapsed: boolean) => {
        isCollapsed.value = collapsed;
    };

    /**
     * 展开侧边栏
     */
    const expand = () => {
        isCollapsed.value = false;
    };

    /**
     * 收起侧边栏
     */
    const collapse = () => {
        isCollapsed.value = true;
    };

    /**
     * 根据路由路径判断是否应该自动收起侧边栏
     * 在这里统一配置需要自动收起的页面
     */
    const shouldAutoCollapse = (path: string): boolean => {
        // 需要自动收起的页面路径列表
        const autoCollapseRoutes = [
            '/patent-drafting',
            // 可以在这里添加更多需要自动收起的页面
            // '/another-page',
        ];

        // 检查当前路径是否匹配需要自动收起的路由
        return autoCollapseRoutes.some(route => {
            // 精确匹配或前缀匹配（支持子路由）
            return path === route || path.startsWith(route + '/');
        });
    };

    /**
     * 根据路由路径判断是否应该自动展开侧边栏
     * 在这里统一配置需要自动展开的页面
     */
    const shouldAutoExpand = (path: string): boolean => {
        // 需要自动展开的页面路径列表
        const autoExpandRoutes = [
            '/',
            '/patent-drafting/projects',
            '/templates',
            '/instructions',
            // '/diagram', // Diagram 功能已临时屏蔽
            '/writing-styles',
            // 可以在这里添加更多需要自动展开的页面
        ];

        // 检查当前路径是否匹配需要自动展开的路由
        return autoExpandRoutes.some(route => {
            return path === route || path.startsWith(route + '/');
        });
    };

    /**
     * 监听路由变化，自动调整侧边栏状态
     */
    watch(
        () => route.path,
        (newPath) => {
            // 优先检查是否需要自动展开（精确匹配优先级更高）
            if (shouldAutoExpand(newPath)) {
                expand();
            }
            // 否则检查是否需要自动收起（前缀匹配）
            else if (shouldAutoCollapse(newPath)) {
                collapse();
            }
            // 如果都不匹配，保持当前状态
        },
        { immediate: true }, // 立即执行一次，处理初始路由
    );

    return {
        // 状态
        isCollapsed,

        // 方法
        toggleSidebar,
        setCollapsed,
        expand,
        collapse,

        // 工具方法（可选暴露，方便外部判断）
        shouldAutoCollapse,
        shouldAutoExpand,
    };
});
